/*!
 * Copyright 2017 - 2020 by ChartIQ, Inc.
 * All rights reserved.
 */
import uuidv4 from "uuid-random";
import { Logger } from "../../clients/logger";
import { RouterClient } from "../../clients/routerClient";
import { BaseService } from "../baseService";
import { SearchProvider } from "./types";

Logger.start();
Logger.system.log("Starting Search Service");

type SearchProviders = Record<string, SearchProvider>;

/**
 * The Search Service
 */
class SearchService extends BaseService {
	searchProviders: SearchProviders;

	constructor() {
		super({
			startupDependencies: {},
			shutdownDependencies: {},
			name: "searchService",
		});
		this.searchProviders = {};

		this.onBaseServiceReady((done: () => void) => {
			this.createRouterEndpoints(done);
		});

		this.start();
	}

	private createRouterEndpoints = (cb: Function): void => {
		RouterClient.addResponder("Search.register", (err, message) => {
			if (!message) {
				Logger.system.error("No message object received for responder: Search.register");
				return;
			}
			Logger.system.log("Search.register", message);
			this.register(message.data);
			const response = err ? null : { status: "success" };
			message.sendQueryResponse(err, response);
		});
		RouterClient.addResponder("Search.unregister", (err, message) => {
			if (!message) {
				Logger.system.error("No message object received for responder: Search.unregister");
				return;
			}
			Logger.system.log("Search.unregister", message);
			if (!message) return;
			this.unregister(message.data);
			const response = err ? null : { status: "success" };
			message.sendQueryResponse(err, response);
		});

		RouterClient.addResponder("Search.search", (err, message) => {
			if (!message) {
				Logger.system.error("No message object received for responder: Search.search");
				return;
			}
			this.search(message.data, (searchErr, res) => {
				message.sendQueryResponse(searchErr, res);
			});
		});

		cb();
	};

	/**
	 * Adds the provider to our list.
	 */
	register = (params: SearchProvider): void => {
		Logger.system.log("register", params);
		this.searchProviders[params.channel] = params; // we register by channel because it's possible to have duplicate providers
	};

	/**
	 * Removes the provider from out list.
	 */
	unregister = (params: SearchProvider): void => {
		delete this.searchProviders[params.channel];
	};

	/**
	 * Searches against the registered search providers.
	 */
	search = (params: SearchProvider, cb: Function) => {
		// Need to add some type of filter.
		const searchId = uuidv4();
		let providerCount = 0; // This just to tell the user how many if they want to know. Maybe should return an object here with names of the providers.
		for (const key in this.searchProviders) {
			providerCount++;
			this.queryProvider(this.searchProviders[key], searchId, params);
		}
		return cb(null, { searchId, providerCount });
	};

	queryProvider(provider: SearchProvider, searchId: string, params: SearchProvider): void {
		// This is the call to each provider
		RouterClient.query(`Search.Provider.${provider.channel}`, params, (err, response) => {
			for (let i = 0; i < response.data.length; i++) {
				response.data[i].provider = provider.channel; // We add this to each result so it's easier to call the action callback
			}
			Logger.system.log("query", provider);
			const returnedResults = {
				provider: {
					displayName: provider.name,
					channel: provider.channel, // We use this to talk back to the provider.
					providerActionTitle: provider.providerActionTitle,
					providerActionCallback: provider.providerActionCallback,
				},
				returnCallback: `${params.windowName}.${provider.name}`,
				searchId,
				data: response.data,
			};

			RouterClient.publish(`Search.${params.windowName}`, returnedResults); // send the results to the window. We use publish since this can be called multiple times. Once for each provider
		});
	}
}

export default new SearchService();
